/**
 * Search-results container for the add-players drawer (SE-6948, PC2).
 *
 * While a query is active the drawer shows results across ALL players, split
 * into the PC2 groups: ALREADY IN THIS PIPELINE (muted "In list" rows) ·
 * RECENTLY VIEWED · ALL PLAYERS with the match count. The split is pure
 * derivation over the rows the search returned (`groupSearchResults`); the
 * recently-viewed membership comes from the same
 * `listRecentlyInteractedPlayers` read the scouting add-player surface uses
 * for its empty state, so "recently viewed" means the same thing on both
 * surfaces.
 * @module features/player-kanban/components/organisms/AddPlayersSearchResults
 */

import React, { useCallback, useMemo } from "react";
import type { NativeScrollEvent, NativeSyntheticEvent } from "react-native";

import type { PlayerSearchPlayerFragment } from "@/generated/graphql";
import { useScoutingListRecentlyInteractedPlayersQuery } from "@/generated/graphql";
import { useAuth } from "@/features/auth";

import { groupSearchResults } from "../../../utils/addPlayersSearchGroups";

import AddPlayersSearchResultsView from "./AddPlayersSearchResultsView";

/** Recently-viewed window, matching the scouting surface's read. */
const RECENT_PLAYERS_LIMIT = 50;
const RECENT_SINCE_DAYS = 30;

/** Pixels from the bottom of the scroll that trigger the next page (SE-7100). */
const SCROLL_THRESHOLD = 200;

/** Props for the AddPlayersSearchResults container. */
export interface AddPlayersSearchResultsContainerProps {
  /**
   * Matches from the ORG-WIDE player search — deliberately not narrowed to
   * the selected tab's pool. That bypass is the feature.
   */
  readonly players: readonly PlayerSearchPlayerFragment[];
  /** The connection's total match count; `null` while unknown. */
  readonly totalCount: number | null;
  /** Whether the first page of results is loading. */
  readonly isLoading: boolean;
  /** Whether a completed search matched nothing. */
  readonly isEmpty: boolean;
  /** Search error, or `null`. */
  readonly error: string | null;
  /** Ids of players already on this pipeline — their rows mute as "In list". */
  readonly pipelinePlayerIds: ReadonlySet<string>;
  /** Player ids with an add in flight. */
  readonly addingPlayerIds: readonly string[];
  /** Add a player, by id. */
  readonly onAdd: (playerId: string) => void;
  /** Fetch the next page of matches (SE-7100). */
  readonly onLoadMore: () => void;
  /** Whether more pages exist beyond the loaded rows (SE-7100). */
  readonly hasMore: boolean;
}

/**
 * Drive the grouped search-results body.
 * @param root0 - Component props.
 * @param root0.players - Search matches.
 * @param root0.totalCount - Total match count.
 * @param root0.isLoading - Whether results are loading.
 * @param root0.isEmpty - Whether the search matched nothing.
 * @param root0.error - Search error.
 * @param root0.pipelinePlayerIds - Players already on this pipeline.
 * @param root0.addingPlayerIds - Ids with an add in flight.
 * @param root0.onAdd - Add callback.
 * @param root0.onLoadMore - Next-page fetch (SE-7100).
 * @param root0.hasMore - Whether more pages exist (SE-7100).
 */
const AddPlayersSearchResultsContainer = ({
  players,
  totalCount,
  isLoading,
  isEmpty,
  error,
  pipelinePlayerIds,
  addingPlayerIds,
  onAdd,
  onLoadMore,
  hasMore,
}: AddPlayersSearchResultsContainerProps) => {
  const auth = useAuth();
  const hasAuth = Boolean(auth?.data?.accessToken);

  /* Mounted only while a search is active, so no isOpen gate is needed. */
  const { data: recentData } = useScoutingListRecentlyInteractedPlayersQuery({
    variables: { limit: RECENT_PLAYERS_LIMIT, sinceDays: RECENT_SINCE_DAYS },
    skip: !hasAuth,
    fetchPolicy: "cache-and-network",
  });

  const recentlyViewedIds = useMemo(
    () =>
      new Set(
        (recentData?.listRecentlyInteractedPlayers ?? []).flatMap(node =>
          node.player == null ? [] : [node.player.id]
        )
      ),
    [recentData?.listRecentlyInteractedPlayers]
  );

  const groups = useMemo(
    () => groupSearchResults(players, pipelinePlayerIds, recentlyViewedIds),
    [pipelinePlayerIds, players, recentlyViewedIds]
  );

  /*
   * SE-7100: near-bottom scroll fetches the next page, the same threshold
   * pattern the filtered-results body uses — so the list can actually reach
   * the caption's "N MATCHES" instead of stopping at the first page.
   */
  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const { layoutMeasurement, contentOffset, contentSize } =
        event.nativeEvent;
      const distanceFromBottom =
        contentSize.height - layoutMeasurement.height - contentOffset.y;
      if (distanceFromBottom < SCROLL_THRESHOLD) onLoadMore();
    },
    [onLoadMore]
  );

  return (
    <AddPlayersSearchResultsView
      inPipeline={groups.inPipeline}
      recentlyViewed={groups.recentlyViewed}
      others={groups.others}
      matchCount={totalCount ?? groups.others.length}
      isLoading={isLoading}
      isEmpty={isEmpty}
      error={error}
      addingPlayerIds={addingPlayerIds}
      onAdd={onAdd}
      onScroll={hasMore ? handleScroll : undefined}
    />
  );
};

AddPlayersSearchResultsContainer.displayName =
  "AddPlayersSearchResultsContainer";

export default AddPlayersSearchResultsContainer;
